import {
  CAPABILITIES_MODULE_ID,
  CAPABILITY_KEYS,
  type ModuleConfigView,
  type PageConfigView,
} from '../../../api/Navigation/moduleConfig';
import type {
  CompanyConfigDelta,
  ModuleConfigDeltaItem,
  PageConfigDeltaItem,
} from '../../../api/Navigation/moduleConfigAdmin';
import {
  navigationModules,
  withoutPreviewPages,
  pageTabGroups,
  mobileModules,
} from '../../../components/layout/navigationModules';

/**
 * Pure functions of the Module Configuration editor (CO-860): how stored rows become switches, which
 * switches differ from what the server holds, and what a save sends.
 *
 * No React and no request in this file. The save is a DELTA: only the keys the Operator changed are
 * sent, so the server never has to guess what an absent key means and no row is deleted because this
 * screen did not know about it.
 */

/** Enabled flags keyed by module id (module map) or by `moduleId:pagePath` (page map). */
export type ConfigStateMap = Record<string, boolean>;

export type ConfigChangeKind = 'module' | 'page' | 'tab' | 'capability' | 'mobile';

/** One switch that differs from what the server last returned or accepted. */
export interface ConfigChange {
  /** Module id for a module-map change; `moduleId:pagePath` for a page-map change. */
  key: string;
  /** Which of the two saved maps the key belongs to. A mobile MODULE and a mobile PAGE are both kind `mobile`. */
  map: 'module' | 'page';
  kind: ConfigChangeKind;
  from: boolean;
  to: boolean;
}

/** Storage key of a page, tab, capability or mobile-page row inside the page map. */
export function pageKey(moduleId: string, pagePath: string): string {
  return `${moduleId}:${pagePath}`;
}

/** Inverse of `pageKey`. Splits on the FIRST colon: a module id never holds one, a path may. */
export function splitPageKey(key: string): { moduleId: string; pagePath: string } {
  const at = key.indexOf(':');
  return at < 0 ? { moduleId: key, pagePath: '' } : { moduleId: key.slice(0, at), pagePath: key.slice(at + 1) };
}

/**
 * A stored `PagePath` that names a web page starts with a slash; tab ids, capability keys and mobile
 * page ids do not. The server's delta endpoint uses the same test to choose between the path-wide
 * rule and the exact `(ModuleId, PagePath)` rule.
 */
export function isWebPagePath(pagePath: string): boolean {
  return pagePath.startsWith('/');
}

const MOBILE_MODULE_IDS = new Set(mobileModules.map(m => m.id));
const TAB_GROUP_IDS = new Set(pageTabGroups.map(g => g.id));

function pageChangeKind(key: string): ConfigChangeKind {
  const { moduleId } = splitPageKey(key);
  if (moduleId === CAPABILITIES_MODULE_ID) return 'capability';
  if (TAB_GROUP_IDS.has(moduleId)) return 'tab';
  if (MOBILE_MODULE_IDS.has(moduleId)) return 'mobile';
  return 'page';
}

/**
 * Build the two editable maps from the registries (default ON) overlaid with the stored rows.
 *
 * Three rules keep the screen equal to what the tenant's users actually get:
 *  - Only switches the screen renders are seeded. `previewOnly` pages have no switch, so they have
 *    no key, and a module row whose id is in neither registry is NOT carried: it stays in the
 *    database untouched (the old save re-wrote such rows on every visit).
 *  - A web page is OFF when ANY stored row holds its path disabled, whatever `ModuleId` that row
 *    sits under. That is the rule `PageConfigGuard` blocks on, and it is what makes a row left
 *    under an older ModuleId (CO-496) show as OFF here instead of as an ON switch over a blocked
 *    page.
 *  - Tabs, capabilities and mobile pages match on the exact `(moduleId, pagePath)` pair, as the
 *    runtime does for them.
 */
export function buildStates(config: {
  modules: ModuleConfigView[];
  pages: PageConfigView[];
}): { mStates: ConfigStateMap; pStates: ConfigStateMap } {
  const mStates: ConfigStateMap = {};
  navigationModules.forEach(module => {
    mStates[module.id] = true;
  });
  mobileModules.forEach(mobileModule => {
    // A module the app hides until an ON row exists reads OFF here too while it has no row.
    mStates[mobileModule.id] = mobileModule.offUnlessOn !== true;
  });
  config.modules.forEach(row => {
    if (row.moduleId in mStates) mStates[row.moduleId] = row.isEnabled;
  });

  const disabledWebPaths = new Set<string>();
  config.pages.forEach(row => {
    if (isWebPagePath(row.pagePath) && !row.isEnabled) disabledWebPaths.add(row.pagePath);
  });

  const pStates: ConfigStateMap = {};
  const webKeys = new Set<string>();
  const seedWebPage = (ownerId: string, path: string) => {
    const key = pageKey(ownerId, path);
    webKeys.add(key);
    pStates[key] = !disabledWebPaths.has(path);
  };
  withoutPreviewPages(navigationModules).forEach(module => {
    module.pages.forEach(page => seedWebPage(module.id, page.path));
    // Sub-module pages are keyed by the sub-module id, as they are stored.
    module.subModules?.forEach(sub => {
      sub.pages.forEach(page => seedWebPage(sub.id, page.path));
    });
  });

  // Tab config shares the page-config storage: moduleId = tab-group id, pagePath = tab id.
  pageTabGroups.forEach(group => {
    group.tabs.forEach(tab => {
      pStates[pageKey(group.id, tab.id)] = true;
    });
  });
  // Capabilities default to allowed, matching the deny-only contract.
  CAPABILITY_KEYS.forEach(key => {
    pStates[pageKey(CAPABILITIES_MODULE_ID, key)] = true;
  });
  mobileModules.forEach(mobileModule => {
    mobileModule.pages.forEach(page => {
      pStates[pageKey(mobileModule.id, page.id)] = true;
    });
  });
  config.pages.forEach(row => {
    const key = pageKey(row.moduleId, row.pagePath);
    if (key in pStates && !webKeys.has(key)) pStates[key] = row.isEnabled;
  });

  return { mStates, pStates };
}

/** The keys of one map whose value differs from the baseline. */
export function diffStates(
  baseline: ConfigStateMap,
  current: ConfigStateMap,
  map: ConfigChange['map']
): ConfigChange[] {
  const changes: ConfigChange[] = [];
  Object.entries(current).forEach(([key, to]) => {
    // Both maps are built by `buildStates` from the same registries, so a key missing from the
    // baseline cannot be produced by a toggle. It is still reported rather than dropped.
    if (key in baseline && baseline[key] === to) return;
    changes.push({
      key,
      map,
      kind: map === 'module' ? (MOBILE_MODULE_IDS.has(key) ? 'mobile' : 'module') : pageChangeKind(key),
      from: baseline[key] ?? true,
      to,
    });
  });
  return changes;
}

/**
 * `fresh` (what the server holds now) with the Operator's own edits laid back on top.
 *
 * An edit is a key whose `current` value differs from `reference`. After a save `reference` is the
 * map that was sent, so only a toggle made while the request was in flight survives. After a reload
 * `reference` is the old baseline, so every staged change survives, and one that the server already
 * holds stops being a change by itself.
 */
export function rebaseStates(
  fresh: ConfigStateMap,
  reference: ConfigStateMap,
  current: ConfigStateMap
): ConfigStateMap {
  const next: ConfigStateMap = { ...fresh };
  Object.keys(fresh).forEach(key => {
    if (key in current && key in reference && current[key] !== reference[key]) next[key] = current[key];
  });
  return next;
}

/**
 * Sidebar position the server stores when it has to INSERT a module row.
 *
 * Both registries, because the screen edits both. Mobile ids are offset past the web ones so the two
 * blocks keep their own order and never interleave. Undefined for an id in neither registry.
 */
export function moduleDisplayOrder(moduleId: string): number | undefined {
  const webIndex = navigationModules.findIndex(m => m.id === moduleId);
  if (webIndex >= 0) return webIndex;
  const mobileIndex = mobileModules.findIndex(m => m.id === moduleId);
  return mobileIndex >= 0 ? navigationModules.length + mobileIndex : undefined;
}

/** The request body of a save: the changed keys only, and the version they were staged against. */
export function buildDelta(changes: ConfigChange[], version: string): CompanyConfigDelta {
  const modules: ModuleConfigDeltaItem[] = [];
  const pages: PageConfigDeltaItem[] = [];
  changes.forEach(change => {
    if (change.map === 'module') {
      const displayOrder = moduleDisplayOrder(change.key);
      modules.push({
        moduleId: change.key,
        isEnabled: change.to,
        ...(displayOrder === undefined ? {} : { displayOrder }),
      });
      return;
    }
    const { moduleId, pagePath } = splitPageKey(change.key);
    pages.push({ moduleId, pagePath, isEnabled: change.to });
  });
  return { version, modules, pages };
}
